import { useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";
import BaseContainer from "../containers/BaseContainer";
import projects from "../data/projects";

function ProjectPage() {
  const { slug } = useParams<{ slug: string }>();
  const project = projects.find((p) => p.slug === slug);

  useEffect(() => {
    if (project) {
      document.title = `${project.name} | Matteo Aries`;
    }
  }, [project]);

  if (!project) {
    return <Navigate to="/" replace />;
  }

  const projectIndex = projects.indexOf(project);

  return (
    <BaseContainer className="flex flex-col gap-12 py-8">
      {/* Back link */}
      <a
        href="/"
        className="inline-block text-sm font-black uppercase tracking-wide text-orange-600 border-b-2 border-orange-600 hover:bg-orange-100 transition-colors self-start"
      >
        ← Back to home
      </a>

      {/* Project header */}
      <div className="flex flex-col gap-4">
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-gray-400">
          System {String(projectIndex + 1).padStart(2, "0")}
        </span>
        <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.9]">
          {project.name}
          <span className="text-orange-600">.</span>
        </h1>
        <p className="text-lg md:text-xl font-bold max-w-[600px] leading-snug">
          {project.tagline}
        </p>
      </div>

      {/* Metadata strip */}
      <div className="border-3 border-black flex flex-col sm:flex-row">
        <div className="flex-1 p-4 sm:border-r-3 sm:border-black border-b-3 sm:border-b-0">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-orange-600 block mb-1">
            Role
          </span>
          <p className="text-sm font-bold">{project.role}</p>
        </div>
        <div className="flex-1 p-4 sm:border-r-3 sm:border-black border-b-3 sm:border-b-0">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-orange-600 block mb-1">
            Stack
          </span>
          <div className="flex flex-wrap gap-2">
            {project.stack.map((tech) => (
              <span
                key={tech}
                className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 border border-black"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
        {project.link && (
          <div className="flex-1 p-4">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-orange-600 block mb-1">
              Live
            </span>
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-bold border-b border-black hover:border-orange-600 hover:text-orange-600 transition-colors"
            >
              {project.link.replace(/^https?:\/\//, "")}
            </a>
          </div>
        )}
      </div>

      {/* Description */}
      <div className="border-l-3 border-black pl-6 max-w-[700px]">
        <p className="text-sm leading-relaxed">{project.description}</p>
      </div>

      {/* Key decisions / highlights */}
      <div className="flex flex-col gap-4">
        <h2 className="text-xl font-black uppercase tracking-tight">
          Key Decisions
          <span className="text-orange-600">.</span>
        </h2>
        <div className="border-3 border-black">
          {project.highlights.map((highlight, i) => (
            <div
              key={i}
              className={`flex items-start gap-4 p-4 ${
                i < project.highlights.length - 1
                  ? "border-b-3 border-black"
                  : ""
              }`}
            >
              <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-gray-400 shrink-0 mt-0.5">
                {String(i + 1).padStart(2, "0")}
              </span>
              <p className="text-sm leading-relaxed">{highlight}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Image gallery */}
      {project.images.length > 0 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-xl font-black uppercase tracking-tight">
            Gallery
            <span className="text-orange-600">.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {project.images.map((img, i) => (
              <div
                key={i}
                className="border-3 border-black shadow-[5px_5px_0px_0px_#000] bg-gray-100 overflow-hidden"
              >
                <img
                  src={img}
                  alt={`${project.name} screenshot ${i + 1}`}
                  className="w-full h-[450px] object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                    target.parentElement!.innerHTML = `
                      <div class="flex items-center justify-center h-[300px] text-center p-6">
                        <div>
                          <p class="text-sm font-bold uppercase tracking-wide text-gray-400">Screenshot</p>
                          <p class="text-xs text-gray-400 mt-1">${project.name} — Image ${i + 1}</p>
                        </div>
                      </div>
                    `;
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Navigation to other projects */}
      <div className="flex flex-col gap-4 mt-8">
        <div className="w-full h-[3px] bg-black" />
        <h2 className="text-sm font-black uppercase tracking-wide text-gray-400">
          Other Systems
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {projects
            .filter((p) => p.slug !== project.slug)
            .map((p) => (
              <a
                key={p.slug}
                href={`/projects/${p.slug}`}
                className="group border-3 border-black p-4 bg-white shadow-[3px_3px_0px_0px_#000] hover:shadow-[3px_3px_0px_0px_#ea580c] hover:border-orange-600 transition-all"
              >
                <h3 className="font-black uppercase tracking-tight group-hover:text-orange-600 transition-colors">
                  {p.name}
                </h3>
                <p className="text-xs mt-1">{p.tagline}</p>
              </a>
            ))}
        </div>
      </div>
    </BaseContainer>
  );
}

export default ProjectPage;
